import React from 'react';
import { useNavigate } from 'react-router-dom';
import { convertWeight, extractApiErrorMessage, notifications, toKilograms } from '../../utils';
import { Loader, ReloadIconButton, Title, TablePaginationWrapper, LegacyGrid as Grid } from '../../elements';
import { AnimalType, WeightType } from '../../models/animal';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import PigletIco from '../../static/piglet01.jpg';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { tokens } from '../../theme/tokens';
import { V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import AnimalViewSlaughterHouse from '../../components/AnimalViewSlaughterhouse';
import AnimalViewWeightsGraph from '../../components/AnimalViewWeightsGraph';
import SowIco from '../../static/sow01.jpg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartSimple,
  faCalendar,
  faWeightHanging,
  faChartLine,
  faChartPie,
  faListOl,
  faChartBar,
  faSkull,
  faWheatAwn
} from '@fortawesome/free-solid-svg-icons';
import { ApiService } from '../../hooks';
import {
  HealthSystemDALEnumsWeightType,
  HealthSystemDALModelAnimalsDataAnimalSlaughterhouseData,
  HealthSystemDALModelAnimalsDataAnimalWeight,
  HealthSystemDALModelDoubleWeightWithDate,
  HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel,
  HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel
} from '../../api/HealthSystemApi';
import {
  AnimalSlaughterhouseData,
  AnimalWeight,
  toAnimalSlaughterhouseData,
  toAnimalWeight
} from '../../models/animal-view';
import { renderWeightCellDoubleWeightWithDate } from '../../utils/animalutils';
import NoDataMessage from '../shared/NoDataMessage';

interface AnimalViewPigletsProps {
  animalId: string;
  animalType: AnimalType;
}

/** The slaughterhouse/weight pair shown in the "view slaughterhouse data" dialogs below - mapped at
 * the point it's read off the fetched birth-cycle/animal DTOs, never the raw generated shape. */
interface SlaughterhouseDialogData {
  slaughterhouseData: AnimalSlaughterhouseData | null;
  weight: AnimalWeight | null;
}

type Phase = 'farrowing' | 'nursery' | 'fatting';

type WeightPoint = 'birthed' | 'weaned' | 'tenWeeks' | 'slaughtered';

const MS_PER_DAY = 86400000;

const PHASES: Record<Phase, { start: WeightPoint; end: WeightPoint }> = {
  farrowing: { start: 'birthed', end: 'weaned' },
  nursery: { start: 'weaned', end: 'tenWeeks' },
  fatting: { start: 'tenWeeks', end: 'slaughtered' }
};

const toDate = (iso?: string): Date | null => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
};

const toGrams = (value?: number, unit?: HealthSystemDALEnumsWeightType): number => {
  return convertWeight(value, unit, 1) || 0;
};

const getEntry = (
  animal: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel,
  key: WeightPoint
): HealthSystemDALModelDoubleWeightWithDate | undefined => animal.weight?.[key];

const hasPositiveWeights = (
  a?: HealthSystemDALModelDoubleWeightWithDate,
  b?: HealthSystemDALModelDoubleWeightWithDate
): boolean => (a?.value ?? 0) > 0 && (b?.value ?? 0) > 0;

const daysBetween = (start?: string, end?: string): number => {
  const sd = toDate(start);
  const ed = toDate(end);
  if (!sd || !ed) return 0;
  const days = (ed.getTime() - sd.getTime()) / MS_PER_DAY;
  return days > 0 ? days : 0;
};

const AnimalViewPiglets = ({ animalId, animalType }: AnimalViewPigletsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [searchResult, setSearchResult] = React.useState<HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel[]>(
    []
  );
  const [isBusy, setIsBusy] = React.useState(false);

  const [page, setPage] = React.useState(0);
  const rowsPerPage = DEFAULT_ROWS_PER_PAGE;
  const [total, setTotal] = React.useState(0);
  const [totalPiglets, setTotalPiglets] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const [loaded, setLoaded] = React.useState(false);

  const loadAnimalBirthCycles = async () => {
    apiService.api.animals
      .animalsGetAnimalPigletsBirthCycles({ AnimalId: animalId, AnimalType: animalType })
      .then((result) => {
        setTotal(result.data.data.length);
        setTotalPiglets(result.data.totalPiglets);
        setSearchResult(result.data.data);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      });
  };

  // go to animal view page (with id param)
  const viewAnimal = (id: string) => {
    navigate(`/animal/${id}`);
  };

  const renderWeightCellNumber = (value: number | null | undefined, displayUnit: WeightType) => {
    if (value === null || value === undefined) {
      return '/';
    }

    if (value > 0) {
      return `${value.toFixed(1)} (${displayUnit === WeightType.Grams ? 'g' : 'kg'})`;
    }

    return '/';
  };

  // Daily growth is a fraction of a kilogram, so it needs more precision than the 1 decimal the
  // other weight cells use.
  const renderDailyGrowthCell = (valueInKg: number | null | undefined) => {
    if (valueInKg === null || valueInKg === undefined || valueInKg <= 0) {
      return '/';
    }

    return `${valueInKg.toFixed(3)} (kg)`;
  };

  const renderDateCell = (date: string | null | undefined) => {
    if (!date) return '/';
    return new Date(date).toLocaleDateString();
  };

  const calculateTotalWeight = (
    animals: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel[],
    weightType: 'birthed' | 'weaned' | 'slaughtered' | 'tenWeeks'
  ) => {
    let total = 0;
    let hasValues = false;

    const targetUnit = WeightType.Kilograms;

    animals.forEach((animal) => {
      const weightData = animal.weight?.[weightType];
      if (weightData?.value != null && weightData.value > 0) {
        hasValues = true;
        const convertedWeight = convertWeight(weightData.value, weightData.unit, targetUnit);
        if (convertedWeight != null) {
          total += convertedWeight;
        }
      }
    });

    if (!hasValues || total === 0) return '/';
    return renderWeightCellNumber(total, targetUnit);
  };

  const calculateAverageDailyGrowth = (
    animals: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel[],
    phase: Phase
  ) => {
    const { start, end } = PHASES[phase];

    const validAnimals = animals.filter((animal) => {
      const s = getEntry(animal, start);
      const e = getEntry(animal, end);
      return hasPositiveWeights(s, e);
    });

    if (validAnimals.length === 0) return '/';

    const totalGrowth = validAnimals.reduce((sum, animal) => {
      const s = getEntry(animal, start);
      const e = getEntry(animal, end);

      const days = daysBetween(s?.date, e?.date);
      if (days <= 0) return sum;

      const startGrams = toGrams(s?.value, s?.unit);
      const endGrams = phase === 'fatting' ? toGrams(e?.value, 2) : toGrams(e?.value, e?.unit);
      const gain = endGrams - startGrams;

      const dailyGrowth = gain / days;
      return sum + (isFinite(dailyGrowth) ? dailyGrowth : 0);
    }, 0);

    const avgGrowth = totalGrowth / validAnimals.length;
    return avgGrowth > 0 ? renderDailyGrowthCell(toKilograms(avgGrowth, WeightType.Grams)) : '/';
  };

  const renderAnimalIsDied = (animal: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel) => {
    if (animal.disease !== null) {
      return t('f-search-view-died') + ': ' + animal.disease?.name;
    }

    return t('f-search-view-died');
  };

  function EventLogTableRow(props: Readonly<{ cycle: HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel }>) {
    const { cycle } = props;
    const [rowExpanded, setRowExpanded] = React.useState(false);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogData, setDialogData] = React.useState<SlaughterhouseDialogData | null>(null);

    const openCycleDetailsDialog = (
      slaughterhouseData: HealthSystemDALModelAnimalsDataAnimalSlaughterhouseData | null | undefined,
      weight: HealthSystemDALModelAnimalsDataAnimalWeight | null | undefined
    ) => {
      setDialogData({
        slaughterhouseData: slaughterhouseData ? toAnimalSlaughterhouseData(slaughterhouseData) : null,
        weight: weight ? toAnimalWeight(weight) : null
      });
      setDialogOpen(true);
    };

    const closeCycleDetailsDialog = () => {
      setDialogOpen(false);
    };

    return (
      <>
        <React.Fragment key={cycle.dateOfBirth}>
          <TableRow key={`btn${cycle.dateOfBirth}`} hover selected={rowExpanded} className="pg-striped">
            <TableCell align="center">#{cycle.birthCycleNumber}</TableCell>
            <TableCell align="center">
              <Tooltip arrow title={rowExpanded ? t('f-birth-cycle-hide') : t('f-birth-cycle-expand')}>
                <IconButton
                  aria-label="expand row"
                  size="small"
                  sx={V4_ICON_BUTTON_SMALL}
                  onClick={() => setRowExpanded(!rowExpanded)}
                >
                  {rowExpanded ? <Icon>{'keyboard_arrow_up'}</Icon> : <Icon>{'keyboard_arrow_down'}</Icon>}
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell>
              <Grid container direction="column">
                {new Date(cycle.dateOfBirth).toLocaleDateString()}
                {cycle.slaughterhouseDataAverage && (
                  <Tooltip arrow title={t('f-piglets-slaughterhouse-view')}>
                    <IconButton
                      aria-label="View slaughterhouse"
                      size="small"
                      sx={V4_ICON_BUTTON_SMALL}
                      onClick={() => openCycleDetailsDialog(cycle.slaughterhouseDataAverage, cycle.weightAverage)}
                    >
                      <FontAwesomeIcon icon={faChartSimple} className="pg-icon" />
                    </IconButton>
                  </Tooltip>
                )}
              </Grid>
            </TableCell>
            <TableCell align="center">{calculateTotalWeight(cycle.animals, 'birthed')}</TableCell>
            <TableCell className="pg-bold" align="center">
              {t('f-piglets-statistics', {
                total: cycle.total,
                dead: cycle.totalDead,
                slaughtered: cycle.totalSlaughtered
              })}
            </TableCell>
            <TableCell className="pg-border-right" align="center">
              {calculateAverageDailyGrowth(cycle.animals, 'farrowing')}
            </TableCell>
            <TableCell className="pg-bold" align="center">
              {t('f-piglets-statistics-total', {
                own: cycle.weaned?.totalOwn,
                foster: cycle.weaned?.totalFoster
              })}
            </TableCell>
            <TableCell align="center">{calculateTotalWeight(cycle.animals, 'weaned')}</TableCell>
            <TableCell className="pg-border-right" align="center">
              {renderWeightCellNumber(cycle.weightAverage?.weaned?.value, WeightType.Kilograms)}
            </TableCell>
            <TableCell align="center">{calculateAverageDailyGrowth(cycle.animals, 'nursery')}</TableCell>
            <TableCell className="pg-border-right" align="center">
              {calculateTotalWeight(cycle.animals, 'tenWeeks')}
            </TableCell>
            <TableCell align="center">{calculateAverageDailyGrowth(cycle.animals, 'fatting')}</TableCell>
            <TableCell align="center">
              {renderWeightCellNumber(cycle.slaughterhouseDataAverage?.weight, WeightType.Kilograms)}
            </TableCell>
            <TableCell className="pg-border-right" align="center">
              {renderWeightCellNumber(cycle.slaughterhouseWeight, WeightType.Kilograms)}
            </TableCell>
            <TableCell align="center">
              <Grid container direction="column" spacing={0}>
                <Grid item>
                  {renderWeightCellNumber(cycle.feedConversion?.gestationFeed?.weight, WeightType.Kilograms)}
                </Grid>
                {cycle.feedConversion?.gestationFeed?.feedConversionRatio != null &&
                  cycle.feedConversion.gestationFeed.feedConversionRatio > 0 && (
                    <Grid item className="pg-secondary">
                      FCR: {cycle.feedConversion.gestationFeed.feedConversionRatio.toFixed(2)}
                    </Grid>
                  )}
              </Grid>
            </TableCell>
            <TableCell className="pg-border-right" align="center">
              <Grid container direction="column" spacing={0}>
                <Grid item>
                  {renderWeightCellNumber(cycle.feedConversion?.lactationFeed?.weight, WeightType.Kilograms)}
                </Grid>
                {cycle.feedConversion?.lactationFeed?.feedConversionRatio != null &&
                  cycle.feedConversion.lactationFeed.feedConversionRatio > 0 && (
                    <Grid item className="pg-secondary">
                      FCR: {cycle.feedConversion.lactationFeed.feedConversionRatio.toFixed(2)}
                    </Grid>
                  )}
              </Grid>
            </TableCell>
            <TableCell align="center">
              {cycle.totalDead + cycle.totalSlaughtered} / {cycle.total}
            </TableCell>
          </TableRow>

          <TableRow key={`row${cycle.dateOfBirth}`}>
            <TableCell className="pg-arrow-cell" colSpan={17}>
              <Collapse in={rowExpanded} timeout="auto" unmountOnExit>
                {renderEventAnimalSection(cycle)}
              </Collapse>
            </TableCell>
          </TableRow>
        </React.Fragment>

        <Dialog
          id="viewAnimalSlaughterhouseAverageDataDialog"
          open={dialogOpen}
          onClose={() => closeCycleDetailsDialog()}
          sx={{ overflow: 'hidden' }}
          maxWidth="lg"
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="viewAnimalSlaughterhouseAverageDataDialog" sx={{ background: tokens.sidebar }}>
            {t('f-piglets-slaughterhouse-view-average', {
              date: new Date(cycle.dateOfBirth ?? new Date()).toLocaleString()
            })}
          </DialogTitle>

          <DialogContent>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {dialogData?.slaughterhouseData && (
                  <Grid item sm={6} xs={12}>
                    <AnimalViewSlaughterHouse slaughterhouseData={dialogData.slaughterhouseData} />
                  </Grid>
                )}

                {dialogData?.weight && (
                  <Grid item sm={6} xs={12}>
                    <AnimalViewWeightsGraph weight={dialogData.weight} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ background: tokens.sidebar }}>
            <Button
              onClick={() => closeCycleDetailsDialog()}
              variant="outlined"
              startIcon={<Icon>{'close'}</Icon>}
              color="primary"
            >
              {t('m-global-close')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  const renderEventAnimalSection = (cycle: HealthSystemDomainAnimalsResultAnimalBirthCycleViewModel) => {
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogData, setDialogData] = React.useState<SlaughterhouseDialogData | null>(null);

    const openAnimalDetailsDialog = (animal: HealthSystemDomainAnimalsResultAnimalBirthCycleAnimalViewModel) => {
      setDialogData({
        slaughterhouseData: animal.slaughterhouseData ? toAnimalSlaughterhouseData(animal.slaughterhouseData) : null,
        weight: animal.weight ? toAnimalWeight(animal.weight) : null
      });
      setDialogOpen(true);
    };

    const closeAnimalDetailsDialog = () => {
      setDialogOpen(false);
    };

    return (
      <>
        <Grid container sx={{ wordBreak: 'break-all', pb: '5px' }}>
          <Grid item xs={12}>
            <Table size="small" sx={pigletsTableSx}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('f-com-animal-number')}</TableCell>
                  <TableCell>{t('f-com-animal-type')}</TableCell>
                  <TableCell>{t('f-animal-birth')}</TableCell>
                  <TableCell>{t('f-animal-weaned')}</TableCell>
                  <TableCell>{t('f-piglets-ten-weeks')}</TableCell>
                  <TableCell>{t('f-animal-slaughtered')}</TableCell>
                  <TableCell>{t('f-animal-daily-grow')}</TableCell>
                  <TableCell>{''}</TableCell>
                  <TableCell>{''}</TableCell>
                </TableRow>
              </TableHead>
              {!isBusy && searchResult && cycle && (
                <TableBody>
                  {cycle.animals.map((animal) => (
                    <TableRow key={animal.id} hover>
                      <TableCell>
                        <Grid container direction="row" alignItems="center">
                          {animal.number}
                          {animal.slaughterhouseData && (
                            <Tooltip arrow title={t('f-piglets-slaughterhouse-view')}>
                              <IconButton
                                aria-label="View slaughterhouse"
                                size="small"
                                sx={V4_ICON_BUTTON_SMALL}
                                onClick={() => openAnimalDetailsDialog(animal)}
                              >
                                <FontAwesomeIcon icon={faChartSimple} className="pg-icon" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Grid>
                      </TableCell>
                      <TableCell>{AnimalType[animal.animalType]}</TableCell>
                      <TableCell>
                        {renderWeightCellDoubleWeightWithDate(animal.weight?.birthed, WeightType.Grams)}
                      </TableCell>
                      <TableCell>
                        <Grid container direction="column" spacing={0}>
                          <Grid item>
                            {renderWeightCellDoubleWeightWithDate(animal.weight?.weaned, WeightType.Kilograms)}
                          </Grid>
                          {animal.weight?.weaned?.date && (
                            <Grid item>{renderDateCell(animal.weight?.weaned?.date)}</Grid>
                          )}
                        </Grid>
                      </TableCell>
                      <TableCell>
                        <Grid item>
                          {renderWeightCellDoubleWeightWithDate(animal.weight?.tenWeeks, WeightType.Kilograms)}
                        </Grid>
                        {animal.weight?.tenWeeks?.date && (
                          <Grid item>{renderDateCell(animal.weight?.tenWeeks?.date)}</Grid>
                        )}
                      </TableCell>
                      <TableCell>
                        {renderWeightCellNumber(animal.slaughterhouseData?.weight, WeightType.Kilograms)}
                      </TableCell>
                      <TableCell>
                        {renderDailyGrowthCell(toKilograms(animal.weight?.weightPerDay, WeightType.Grams))}
                      </TableCell>
                      <TableCell>
                        {animal.isDied && (
                          <Chip
                            color="secondary"
                            sx={{ m: 1 }}
                            size="small"
                            label={renderAnimalIsDied(animal)}
                            variant="outlined"
                          />
                        )}

                        {animal.isSoftDeleted && (
                          <Chip sx={{ m: 1 }} size="small" label={t('f-search-view-deleted')} variant="outlined" />
                        )}

                        {animal.fosterMotherId === animalId ? (
                          <Chip sx={{ m: 1 }} size="small" label={t('f-animal-mom-adopted')} variant="outlined" />
                        ) : animal.fosterMotherId ? (
                          <Chip
                            sx={{ m: 1 }}
                            avatar={<Avatar src={SowIco} />}
                            label={t('f-animal-view-mom-foster')}
                            color="primary"
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              navigate(`/animal/${animal.fosterMotherId}`);
                            }}
                          />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Tooltip arrow title={t('f-search-view-animal')}>
                          <IconButton
                            color="primary"
                            aria-label="View animal"
                            size="small"
                            sx={V4_ICON_BUTTON_SMALL}
                            onClick={() => viewAnimal(animal.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </Grid>
        </Grid>

        <Dialog
          id="viewAnimalSlaughterhouseDataDialog"
          open={dialogOpen}
          onClose={() => closeAnimalDetailsDialog()}
          sx={{ overflow: 'hidden' }}
          maxWidth="lg"
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="viewAnimalSlaughterhouseDataDialog" sx={{ background: tokens.sidebar }}>
            {t('f-piglets-slaughterhouse-view')}
          </DialogTitle>

          <DialogContent>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {dialogData?.slaughterhouseData && (
                  <Grid item sm={6} xs={12}>
                    <AnimalViewSlaughterHouse slaughterhouseData={dialogData.slaughterhouseData} />
                  </Grid>
                )}

                {dialogData?.weight && (
                  <Grid item sm={6} xs={12}>
                    <AnimalViewWeightsGraph weight={dialogData.weight} />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ background: tokens.sidebar }}>
            <Button
              onClick={() => closeAnimalDetailsDialog()}
              variant="outlined"
              startIcon={<Icon>{'close'}</Icon>}
              color="primary"
            >
              {t('m-global-close')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => {
        loadAnimalBirthCycles();
        setLoaded(false);
      };

      loadData().finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
    }
  }, [loaded]);

  return (
    <>
      <Paper sx={{ p: 2, display: 'flex', overflow: 'auto', flexDirection: 'column', minHeight: 200 }} elevation={1}>
        <Grid container spacing={1}>
          <Grid container direction="row">
            <Grid container item spacing={1} xs={10}>
              <Grid item>
                <Avatar alt="Animal type" src={PigletIco} />
              </Grid>
              <Grid item>
                <Title>{t('f-piglets')}</Title>
              </Grid>
              <Grid item>
                <Chip label={totalPiglets} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item xs={2} sx={{ textAlign: 'right' }}>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {searchResult.length > 0 ? (
              <TablePaginationWrapper
                rowsPerPageOptions={[DEFAULT_ROWS_PER_PAGE]}
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
              >
                <Paper elevation={1} sx={{ overflowX: 'auto', maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
                  <Table size="small" stickyHeader sx={pigletsTableSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell colSpan={3} className="pg-group-header" align="center">
                          {t('f-piglets-birth-cycle')}
                        </TableCell>
                        <TableCell colSpan={3} className="pg-group-header pg-border-right" align="center">
                          {t('f-animal-birth')}
                        </TableCell>
                        <TableCell colSpan={3} className="pg-group-header pg-border-right" align="center">
                          {t('f-animal-weaned')}
                        </TableCell>
                        <TableCell colSpan={2} className="pg-group-header pg-border-right" align="center">
                          {t('f-piglets-ten-weeks')}
                        </TableCell>
                        <TableCell colSpan={3} className="pg-group-header pg-border-right" align="center">
                          {t('f-animal-slaughtered')}
                        </TableCell>
                        <TableCell colSpan={2} className="pg-group-header pg-border-right" align="center">
                          {t('f-piglets-feed-conversion')}
                        </TableCell>
                        <TableCell className="pg-group-header" align="center">
                          {t('f-com-total')}
                        </TableCell>
                      </TableRow>
                      <TableRow className="pg-header">
                        <Tooltip title={t('f-piglets-birth-cycle')} arrow>
                          <TableCell className="pg-min80" align="center">
                            #
                          </TableCell>
                        </Tooltip>
                        <TableCell className="pg-min80">{''}</TableCell>
                        <Tooltip title={t('f-com-date-of-birth')} arrow>
                          <TableCell className="pg-min120" align="center">
                            <FontAwesomeIcon icon={faCalendar} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-total-birth-weight')} arrow>
                          <TableCell className="pg-min100" align="center">
                            <FontAwesomeIcon icon={faWeightHanging} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-animal-statistics')} arrow>
                          <TableCell className="pg-min120" align="center">
                            <FontAwesomeIcon icon={faChartPie} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-grow-farrowing')} arrow>
                          <TableCell className="pg-min100 pg-border-right" align="center">
                            <FontAwesomeIcon icon={faChartLine} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-animal-statistics-total')} arrow>
                          <TableCell className="pg-min120" align="center">
                            <FontAwesomeIcon icon={faListOl} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-total-weaned-weight')} arrow>
                          <TableCell className="pg-min100" align="center">
                            <FontAwesomeIcon icon={faWeightHanging} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-avg-weaned-weight')} arrow>
                          <TableCell className="pg-min100 pg-border-right" align="center">
                            <FontAwesomeIcon icon={faChartBar} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-grow-nursery')} arrow>
                          <TableCell className="pg-min100" align="center">
                            <FontAwesomeIcon icon={faChartLine} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-ten-weeks-weight')} arrow>
                          <TableCell className="pg-min120 pg-border-right" align="center">
                            <FontAwesomeIcon icon={faWeightHanging} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-grow-fatting')} arrow>
                          <TableCell className="pg-min100" align="center">
                            <FontAwesomeIcon icon={faChartLine} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-animal-slaughterhouse-average-weight')} arrow>
                          <TableCell className="pg-min120" align="center">
                            <FontAwesomeIcon icon={faChartBar} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-animal-slaughterhouse-weight')} arrow>
                          <TableCell className="pg-min120 pg-border-right" align="center">
                            <FontAwesomeIcon icon={faWeightHanging} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-gestation-feed')} arrow>
                          <TableCell className="pg-min100" align="center">
                            <FontAwesomeIcon icon={faWheatAwn} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-lactation-feed')} arrow>
                          <TableCell className="pg-min100 pg-border-right" align="center">
                            <FontAwesomeIcon icon={faWheatAwn} />
                          </TableCell>
                        </Tooltip>
                        <Tooltip title={t('f-piglets-died-slaughtered')} arrow>
                          <TableCell className="pg-min120" align="center">
                            <FontAwesomeIcon icon={faSkull} /> / <FontAwesomeIcon icon={faChartSimple} />
                          </TableCell>
                        </Tooltip>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {searchResult
                        .sort((a, b) => a.birthCycleNumber - b.birthCycleNumber)
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((cycle, index) => (
                          <EventLogTableRow key={index} cycle={cycle} />
                        ))}
                    </TableBody>
                  </Table>
                </Paper>
              </TablePaginationWrapper>
            ) : (
              loaded && !isBusy && <NoDataMessage text={t('f-no-data-to-display')} />
            )}
            <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default AnimalViewPiglets;

/**
 * The piglets tables' cell styles. They live on the table, keyed after V4_TABLE, so they meet v4's cell padding in
 * the order they did as makeStyles classes: a class beats the padding, the last-cell rule beats the class.
 */
const pigletsTableSx = (theme: Theme) => ({
  ...V4_TABLE,
  // v4's sticky header cells sat on background.default, which v4 made #fafafa; v9 made it white.
  '& .MuiTableCell-stickyHeader': { backgroundColor: '#fafafa' },
  '& .pg-header': { background: tokens.sidebar, position: 'sticky', top: 0, zIndex: 10 },
  '& .pg-arrow-cell': { paddingBottom: 0, paddingTop: 0 },
  '& .pg-group-header': {
    background: tokens.sidebar,
    fontWeight: 600,
    fontSize: '0.9em',
    borderBottom: `2px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    zIndex: 10
  },
  '& .pg-border-right': { borderRight: `2px solid ${theme.palette.divider}` },
  '& .pg-min80': { minWidth: 50, padding: '4px 6px' },
  '& .pg-min100': { minWidth: 60, padding: '4px 6px' },
  '& .pg-min120': { minWidth: 75, padding: '4px 6px' },
  '& .pg-striped:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
  '& .pg-bold': { fontWeight: 'bold' },
  '& .pg-icon': { color: theme.palette.primary.main },
  '& .pg-secondary': { fontSize: '0.85em', color: '#666' }
});
